import { faReceipt } from '@fortawesome/pro-light-svg-icons'
import { CashRegisterModelType } from '@teinor/erp/types/company/cashRegister'
import { useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import { DisplayPriceGettingParams } from '../../../../../../../baseComponents/Displays/DisplayPrice'
import NullComponent from '../../../../../../../baseComponents/NullComponent'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import TwinTable from '../../../../../../../baseComponents/TwinTable'
import BaseScreenTable from '../../../../../../../baseComponents/TwinTable/BaseScreenTable'
import { TableComponentProps } from '../../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderDateHours } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import { getISOCurrencyMain, getPaymentTypes } from '../../../../../../../utils/reducers/getters'
import { PaymentTypesPayload } from '../../../../../../../utils/reducers/payments/paymentTypes'
import ModalCashRegisterInfoConnect from './ModalCashRegisterInfo'
import ModalPrintCashRegister from './ModalPrintCashRegister'


interface CashRegistersProps extends ComponentWithPermissions { }

const CashRegisters: React.FC<CashRegistersProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()
    const pageData = {
        title: t('cashRegisters', 'Registros de caja'),
        newText: '',
    }
    return (
        <BaseScreenTable TableComponent={TableCashRegisters} pageData={pageData} userPermissions={userPermissions} EditModalComponent={ModalCashRegisterInfoConnect} CreateModalComponent={NullComponent} DeleteModalComponent={ModalPrintCashRegister} />
    )
}

type TableCashRegistersProps = TableComponentProps & {}

const TableCashRegisters: React.FC<TableCashRegistersProps> = ({ setTableInstance, setOpened}) => {
    const { t } = useTwinTranslation()
    const mainCurrency = getISOCurrencyMain()
    const tpvMethods = useMemo(() => getPaymentTypes()?.activeTpv || [], [])
    let extraColumns: ColumnTableSchema<CashRegisterModelType> = []
    if(tpvMethods){
        for (const element of tpvMethods) {
            extraColumns = extraColumns.concat([
                {
                    id: 'total_quantity'+element.id,
                    dataKey: 'totals',
                    label: t('nTickets', 'Nº Tickets') + ' ' + element.name,
                    customRender: (parameterValue: any) => <span>{parameterValue?.totalsByPayment?.[element.id]?.myQuantity || 0}</span>,
                    hideColumnByDefault: true
                },
                {
                    id: 'total_quantity_refunds'+element.id,
                    dataKey: 'totals',
                    label: t('nRefundsShort', 'Nº Dev.') + ' ' + element.name,
                    customRender: (parameterValue: any) => <span>{parameterValue?.totalsByPayment?.[element.id]?.myQuantityRefund || 0}</span>,
                    hideColumnByDefault: true
                },
                {
                    id: 'total_sells'+element.id,
                    dataKey: 'totals',
                    label: t('totalSells', 'Total ventas') + ' ' + element.name,
                    customRender: (parameterValue: any) => <DisplayPriceGettingParams value={parameterValue?.totalsByPayment?.[element.id]?.myTotalSells || 0} CurrencyId={mainCurrency} with_vat={false} />,
                    hideColumnByDefault: true
                },
                {
                    id: 'total_refunds'+element.id,
                    dataKey: 'totals',
                    label: t('totalRefundsShort', 'Total dev.') + ' ' + element.name,
                    customRender: (parameterValue: any) => <DisplayPriceGettingParams value={parameterValue?.totalsByPayment?.[element.id]?.myTotalRefund || 0} CurrencyId={mainCurrency} with_vat={false} />,
                    hideColumnByDefault: true
                },
                {
                    id: 'totals_total'+element.id,
                    dataKey: 'totals',
                    label: t('totalClean', 'Total neto') + ' ' + element.name,
                    customRender: (parameterValue: any) => <DisplayPriceGettingParams value={parameterValue?.totalsByPayment?.[element.id]?.myTotal || 0} CurrencyId={mainCurrency} with_vat={false} />,
                    hideColumnByDefault: true
                },
            ])
        }
    }

    const columns: ColumnTableSchema<CashRegisterModelType> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            width: 75,
            sortable: true
        },
        {
            id: 'createdAt',
            dataKey: 'createdAt',
            label: t('openDate', 'Fecha de apertura'),
            sortable: true,
            customRender: (parameterValue) => <CustomRenderDateHours value={parameterValue} />
        },
        {
            id: 'OpenEmployee',
            dataKey: 'OpenEmployee',
            label: t('openedFBy', 'Abierta por'),
            customRender: (parameterValue: any) => <span>{parameterValue?.fullname_short}</span>
        },
        {
            id: 'endDate',
            dataKey: 'endDate',
            label: t('closeDate', 'Fecha cierre'),
            sortable: true,
            customRender: (parameterValue) => <CustomRenderDateHours value={parameterValue} />
        },
        {
            id: 'CloseEmployee',
            dataKey: 'CloseEmployee',
            label: t('closedFBy', 'Cerrada por'),
            customRender: (parameterValue: any) => <span>{parameterValue?.fullname_short}</span>
        },
        {
            id: 'total_quantity',
            dataKey: 'totals',
            label: t('nTickets', 'Nº Tickets'),
            customRender: (parameterValue: any) => <span>{parameterValue?.byTotals?.totalQuantity || 0}</span>
        },
        {
            id: 'total_quantity_refunds',
            dataKey: 'totals',
            label: t('nRefunds', 'Nº Devoluciones'),
            customRender: (parameterValue: any) => <span>{parameterValue?.byTotals?.totalQuantityRefund || 0}</span>
        },
        {
            id: 'total_sells',
            dataKey: 'totals',
            label: t('totalSells', 'Total ventas'),
            customRender: (parameterValue: any) => <DisplayPriceGettingParams value={parameterValue?.byTotals?.totalPaymentSells || 0} CurrencyId={mainCurrency} with_vat={false}/>
        },
        {
            id: 'total_refunds',
            dataKey: 'totals',
            label: t('totalRefunds', 'Total devoluciones'),
            customRender: (parameterValue: any) => <DisplayPriceGettingParams value={parameterValue?.byTotals?.totalPaymentRefund || 0} CurrencyId={mainCurrency} with_vat={false}/>
        },
        {
            id: 'totals_total',
            dataKey: 'totals',
            label: t('totalClean', 'Total neto'),
            customRender: (parameterValue: any) => <DisplayPriceGettingParams value={parameterValue?.byTotals?.totalPayment || 0} CurrencyId={mainCurrency} with_vat={false}/>
        },
        ...extraColumns,
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <TwinIcon key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(e) => {e.stopPropagation(); e.preventDefault(); setOpened({ type: 'delete', allRowData })}} icon={faReceipt} />,
        }
    ]
    return (
        <TwinTable name='CashRegisters' columns={columns} getDataFrom='/api/app/order/tpv/cashRegister/getAllCashRegisters' setTableInstance={setTableInstance} onRowClick={(_idk, allRowData) => setOpened({ type: 'edit', allRowData })} />
    )
}

const paymentTypesDispatch = {
    setPaymentTypes: (payload: PaymentTypesPayload) => ({ type: 'CHANGE_PAYMENTTYPES', payload }),
}

export type ReduxPaymentTypes = ConnectedProps<typeof PaymentTypesConnect>
const mapPaymentTypesConnector = (state: AllReduxPayloads) => ({ paymentTypes: state.paymentTypes })
const PaymentTypesConnect = connect(mapPaymentTypesConnector, paymentTypesDispatch)
const PaymentTypesConnectLoading = withLoading(CashRegisters, [{ fetchUrl: '/api/app/config/payment/paymentType/getAllPaymentTypes', propName: 'paymentTypes', setFunctionName: 'setPaymentTypes' }])

export default PaymentTypesConnect(PaymentTypesConnectLoading)